import {
  addShownPointIds,
  removeShownPointIds,
} from "portal/state/farmExplorer";
import { PointList } from "./PointList";
import { Section } from "portal/components/map/navigation/Section";
import { Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useFarmExplorer } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { Zone as ZoneType } from "protos/farm/farm";
import React, { FC, useMemo } from "react";

interface PathProps {
  zone: ZoneType;
}

export const Path: FC<PathProps> = ({ zone }: PathProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { farm } = useFarmExplorer();
  const path = zone.contents?.path;

  const zoneNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const z of farm?.zones ?? []) {
      if (z.id?.id && z.name) {
        names.set(z.id.id, z.name);
      }
    }
    return names;
  }, [farm]);

  const startName = path?.startZoneId?.id
    ? zoneNameById.get(path.startZoneId.id)
    : undefined;
  const endName = path?.endZoneId?.id
    ? zoneNameById.get(path.endZoneId.id)
    : undefined;

  const { linePoints, lineIds } = useMemo(() => {
    const lineIds: string[] = [];
    const linePoints = zone.areas.flatMap((area) => {
      return area.lineString?.points ?? area.polygon?.boundary?.points ?? [];
    });
    for (const p of linePoints) {
      if (p.id?.id) {
        lineIds.push(p.id.id);
      }
    }
    return { linePoints, lineIds };
  }, [zone]);

  return (
    <div className="flex flex-col">
      {path && (
        <Section header={t("views.farms.zoneTypes.path")}>
          <div className="flex gap-1 px-2">
            <Typography className="flex my-auto">
              {t("views.farms.detailsPanel.fromLocation")}
            </Typography>
            <div className="flex ml-auto">
              <Typography className="flex my-auto">
                {startName || path.startingLocationDescription || "—"}
              </Typography>
            </div>
          </div>
          <div className="flex gap-1 px-2">
            <Typography className="flex my-auto">
              {t("views.farms.detailsPanel.toLocation")}
            </Typography>
            <div className="flex ml-auto">
              <Typography className="flex my-auto">
                {endName || path.endingLocationDescription || "—"}
              </Typography>
            </div>
          </div>
        </Section>
      )}
      <Section
        header={t("views.farms.detailsPanel.points")}
        startCollapsed
        onExpand={() => dispatch(addShownPointIds(lineIds))}
        onCollapse={() => dispatch(removeShownPointIds(lineIds))}
      >
        <PointList points={linePoints} />
      </Section>
    </div>
  );
};
